import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { Shield, Box, ShieldCheck, Tags, Clock, ArrowRight, Zap, Lock, RefreshCw } from 'lucide-react'
import SearchInput from '@/components/SearchInput'
import ImageCard from '@/components/ImageCard'
import StatsCard from '@/components/StatsCard'
import Loading from '@/components/Loading'
import { useImages, useStats } from '@/hooks/useImages'

export default function Home() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const { images, isLoading } = useImages()
  const { data: stats } = useStats()

  const handleSearch = (value: string) => {
    setSearch(value)
    if (value) {
      navigate(`/search?q=${encodeURIComponent(value)}`)
    }
  }

  const featuredImages = images.slice(0, 6)

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/50 via-dark-950 to-accent-950/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-900/30 border border-accent-700 text-accent-400 text-sm font-medium mb-6">
              <ShieldCheck className="h-4 w-4" />
              Zero CVEs, Every Image, Every Day
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-dark-100 to-dark-300 bg-clip-text text-transparent">
              CVE-Free Container Images
            </h1>

            <p className="text-lg md:text-xl text-dark-300 mb-8 max-w-2xl mx-auto">
              Secure, minimal container images built fresh daily with automated vulnerability scanning.
              Drop-in replacements for popular images, completely free.
            </p>

            <div className="max-w-xl mx-auto mb-8">
              <SearchInput
                value={search}
                onChange={handleSearch}
                placeholder="Search for images (e.g., node, python, nginx)"
              />
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/images" className="btn-primary inline-flex items-center gap-2">
                Browse Images
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="https://github.com/armoredgate/forge"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                View on GitHub
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-dark-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatsCard
              icon={Box}
              label="Container Images"
              value={stats?.totalImages ?? images.length}
            />
            <StatsCard
              icon={Tags}
              label="Image Tags"
              value={stats?.totalTags ?? '-'}
            />
            <StatsCard
              icon={ShieldCheck}
              label="Zero CVE Images"
              value={stats?.zeroCveImages ?? images.length}
              accent
            />
            <StatsCard
              icon={Clock}
              label="Last Scan"
              value="Today"
            />
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why ArmoredForge?</h2>
            <p className="text-dark-400 max-w-2xl mx-auto">
              Enterprise-grade security without the enterprise price tag
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card p-6">
              <div className="p-3 rounded-lg bg-accent-900/30 text-accent-400 w-fit mb-4">
                <Lock className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Zero CVEs</h3>
              <p className="text-dark-400">
                Every image is scanned with Grype and Trivy. Images with any CVE are blocked from publishing.
              </p>
            </div>

            <div className="card p-6">
              <div className="p-3 rounded-lg bg-primary-900/30 text-primary-400 w-fit mb-4">
                <RefreshCw className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Built Fresh Daily</h3>
              <p className="text-dark-400">
                Automated pipelines rebuild images every day, incorporating the latest security patches.
              </p>
            </div>

            <div className="card p-6">
              <div className="p-3 rounded-lg bg-purple-900/30 text-purple-400 w-fit mb-4">
                <Zap className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Minimal & Fast</h3>
              <p className="text-dark-400">
                Distroless images with only what you need. Smaller attack surface, faster pulls.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-white">Featured Images</h2>
              <p className="text-dark-400">Popular container images, secured</p>
            </div>
            <Link to="/images" className="btn-ghost inline-flex items-center gap-2">
              View All
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {isLoading ? (
            <Loading />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredImages.map(image => (
                <ImageCard key={image.name} image={image} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-8 md:p-12 text-center bg-gradient-to-br from-primary-950/50 to-dark-900">
            <Shield className="h-12 w-12 text-primary-500 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to secure your containers?
            </h2>
            <p className="text-dark-300 mb-8 max-w-2xl mx-auto">
              Start pulling CVE-free images today. No signup required, completely free.
            </p>
            <div className="terminal max-w-xl mx-auto text-left">
              <code className="text-accent-400">
                <span className="text-dark-500">$ </span>
                docker pull forge.armored.app/node:22
              </code>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
